import { inject } from '@angular/core';
import { type CanActivateFn, Router } from '@angular/router';
import { AuthStore } from '@core/store/core/auth.store';

export const AuthorizationGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const authStore = inject(AuthStore);

  // Get required permission from route data
  const requiredPermission = route.data['permission'] as string | string[] | undefined;
  const requireAll = route.data['requireAll'] !== false; // Default to true

  // If no permission is specified, allow access (route is public or handled by other guards)
  if (!requiredPermission) {
    return true;
  }

  // Extract unique resources from the permission strings
  const permissions = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
  const resources = [...new Set(permissions.map((p) => p.split(':')[0]).filter(Boolean))];

  // Trigger lazy loading for any resources that haven't been fetched yet
  await Promise.all(resources.map((resource) => authStore.loadFeaturesForResource(resource!)));

  // Check if user has the required permission(s)
  const checkPermission = (permission: string): boolean => {
    const [resource, action] = permission.split(':');
    if (!resource || !action) return false;
    const actions = authStore.getResourceActions(resource);
    return actions !== undefined && actions.includes(action);
  };

  let hasPermission: boolean;

  if (Array.isArray(requiredPermission)) {
    if (requireAll) {
      hasPermission = requiredPermission.every(checkPermission);
    } else {
      hasPermission = requiredPermission.some(checkPermission);
    }
  } else {
    hasPermission = checkPermission(requiredPermission);
  }

  if (hasPermission) {
    return true;
  }

  // User doesn't have permission, redirect to forbidden page
  router.navigate(['/forbidden']);
  return false;
};
